import { useState } from 'react';
import {
    Box,
    Typography,
    Paper,
    TextField,
    Button,
    IconButton,
    Stack,
    CircularProgress,
    Alert,
    Snackbar,
    Avatar,
    InputAdornment,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    Chip,
    Tooltip
} from '@mui/material';
import { Edit, Refresh, Search, Add, DeleteOutline, Clear } from '@mui/icons-material';
import { useQueryClient } from '@tanstack/react-query';

// Hooks
import {
    useProductCategoriesForSelection,
    useCreateProductCategory,
    useUpdateProductCategory,
    useDeleteProductCategory
} from 'hooks/useProductCategories';
import { useDebounce } from 'hooks/useDebounce';

export default function ProductCategory() {
    const queryClient = useQueryClient();

    // 1. Pagination & Search State
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [searchTerm, setSearchTerm] = useState('');

    // 2. Debounce the search term
    const debouncedSearch = useDebounce(searchTerm, 500);

    // 3. UI State
    const [openModal, setOpenModal] = useState(false);
    const [openDeleteConfirm, setOpenDeleteConfirm] = useState(false);
    const [editMode, setEditMode] = useState(false);
    const [selectedId, setSelectedId] = useState(null);
    const [formData, setFormData] = useState({ name: '', description: '' });
    const [toast, setToast] = useState({ open: false, message: '', severity: 'success' });

    // 4. Data Fetching
    const {
        data: categoriesRes,
        isLoading, // True ONLY on first load (no data in cache)
        isFetching, // True EVERY time a search or refresh happens
        refetch
    } = useProductCategoriesForSelection({
        page: page + 1,
        limit: rowsPerPage,
        search: debouncedSearch
    });

    const categories = categoriesRes?.data || [];
    const totalCategories = categoriesRes?.total || 0;

    // 5. Mutations
    const createMutation = useCreateProductCategory({
        onSuccess: () => handleSuccess('Category created successfully!')
    });

    const updateMutation = useUpdateProductCategory({
        onSuccess: () => handleSuccess('Category updated successfully!')
    });

    const deleteMutation = useDeleteProductCategory({
        onSuccess: () => {
            queryClient.invalidateQueries(['productCategories']);
            setOpenDeleteConfirm(false);
            setToast({ open: true, message: 'Category deleted!', severity: 'error' });
        }
    });

    // 6. Handlers
    const handleSuccess = (msg) => {
        queryClient.invalidateQueries(['productCategories']);
        closeForm();
        setToast({ open: true, message: msg, severity: 'success' });
    };

    const handleSearchChange = (e) => {
        setSearchTerm(e.target.value);
        setPage(0);
    };

    const closeForm = () => {
        setOpenModal(false);
        setFormData({ name: '', description: '' });
        setSelectedId(null);
    };

    // --- UX IMPROVEMENT: Background loading state ---
    // Only block the whole screen if it's the very first time we are getting data
    if (isLoading && !isFetching) {
        return (
            <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" height="70vh">
                <CircularProgress size={40} />
                <Typography sx={{ mt: 2 }} color="text.secondary">
                    Initializing Categories...
                </Typography>
            </Box>
        );
    }

    return (
        <Box p={{ xs: 2, md: 4 }}>
            {/* Header Section */}
            <Paper sx={{ p: 3, mb: 3, borderRadius: 3 }}>
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} justifyContent="space-between" alignItems="center">
                    <Box>
                        <Typography variant="h5" fontWeight={800}>
                            Product Categories
                        </Typography>
                        <Typography color="text.secondary" variant="body2">
                            {isFetching ? 'Searching...' : `Total: ${totalCategories} items`}
                        </Typography>
                    </Box>

                    <Stack direction="row" spacing={2} sx={{ width: { xs: '100%', md: 'auto' } }}>
                        <TextField
                            size="small"
                            placeholder="Search..."
                            value={searchTerm}
                            onChange={handleSearchChange}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <Search fontSize="small" />
                                    </InputAdornment>
                                ),
                                endAdornment: (
                                    <InputAdornment position="end">
                                        {isFetching && <CircularProgress size={16} sx={{ mr: 1 }} />}
                                        {searchTerm && (
                                            <IconButton
                                                size="small"
                                                onClick={() => {
                                                    setSearchTerm('');
                                                    setPage(0);
                                                }}
                                            >
                                                <Clear fontSize="small" />
                                            </IconButton>
                                        )}
                                    </InputAdornment>
                                )
                            }}
                        />
                        <Button
                            variant="contained"
                            startIcon={<Add />}
                            onClick={() => {
                                setEditMode(false);
                                setOpenModal(true);
                            }}
                        >
                            Add New
                        </Button>
                        <IconButton onClick={() => refetch()} disabled={isFetching}>
                            <Refresh className={isFetching ? 'spin-animation' : ''} />
                        </IconButton>
                    </Stack>
                </Stack>
            </Paper>

            {/* List Section */}
            <TableContainer
                component={Paper}
                sx={{
                    borderRadius: 3,
                    position: 'relative',
                    // UX: Dim the table while fetching so the user knows it's updating
                    opacity: isFetching ? 0.6 : 1,
                    transition: 'opacity 0.2s ease'
                }}
            >
                <Table>
                    <TableHead sx={{ bgcolor: '#fafafa' }}>
                        <TableRow>
                            <TableCell sx={{ fontWeight: 700 }}>Category Name</TableCell>
                            <TableCell sx={{ fontWeight: 700 }}>Description</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 700 }}>
                                Type
                            </TableCell>
                            <TableCell align="right" sx={{ fontWeight: 700 }}>
                                Actions
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {categories.map((cat) => (
                            <TableRow key={cat._id} hover>
                                <TableCell>
                                    <Stack direction="row" spacing={2} alignItems="center">
                                        <Avatar sx={{ bgcolor: 'primary.light', width: 32, height: 32, fontSize: '0.9rem' }}>
                                            {cat.name.charAt(0)}
                                        </Avatar>
                                        <Typography variant="subtitle2" fontWeight={600}>
                                            {cat.name}
                                        </Typography>
                                    </Stack>
                                </TableCell>
                                <TableCell>
                                    <Typography variant="body2" color="text.secondary" noWrap sx={{ maxWidth: 250 }}>
                                        {cat.description}
                                    </Typography>
                                </TableCell>
                                <TableCell align="right">
                                    <Chip
                                        label={cat.vendor ? 'My Category' : 'Platform'}
                                        size="small"
                                        color={cat.vendor ? 'primary' : 'default'}
                                        variant={cat.vendor ? 'filled' : 'outlined'}
                                    />
                                </TableCell>
                                <TableCell align="right">
                                    <Tooltip title={!cat.vendor ? 'Platform categories cannot be edited' : ''}>
                                        <span>
                                            <IconButton
                                                onClick={() => {
                                                    setEditMode(true);
                                                    setSelectedId(cat._id);
                                                    setFormData({ name: cat.name, description: cat.description });
                                                    setOpenModal(true);
                                                }}
                                                color="primary"
                                                disabled={!cat.vendor}
                                            >
                                                <Edit fontSize="small" />
                                            </IconButton>
                                        </span>
                                    </Tooltip>
                                    <Tooltip title={!cat.vendor ? 'Platform categories cannot be deleted' : ''}>
                                        <span>
                                            <IconButton
                                                onClick={() => {
                                                    setSelectedId(cat._id);
                                                    setOpenDeleteConfirm(true);
                                                }}
                                                color="error"
                                                disabled={!cat.vendor}
                                            >
                                                <DeleteOutline fontSize="small" />
                                            </IconButton>
                                        </span>
                                    </Tooltip>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>

                {/* Empty State */}
                {!isFetching && categories.length === 0 && (
                    <Box p={5} textAlign="center">
                        <Typography color="text.secondary">No categories found.</Typography>
                    </Box>
                )}

                <TablePagination
                    component="div"
                    count={totalCategories}
                    page={page}
                    onPageChange={(e, p) => setPage(p)}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={(e) => {
                        setRowsPerPage(parseInt(e.target.value, 10));
                        setPage(0);
                    }}
                />
            </TableContainer>

            {/* Modals & Toast remain the same logic... */}
            <Dialog open={openModal} onClose={closeForm} fullWidth maxWidth="xs">
                <DialogTitle>{editMode ? 'Edit Category' : 'New Category'}</DialogTitle>
                <form
                    onSubmit={(e) => {
                        e.preventDefault();
                        editMode ? updateMutation.mutate({ id: selectedId, ...formData }) : createMutation.mutate(formData);
                    }}
                >
                    <DialogContent dividers>
                        <Stack spacing={2}>
                            <TextField
                                label="Name"
                                fullWidth
                                value={formData.name}
                                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                required
                            />
                            <TextField
                                label="Description"
                                fullWidth
                                multiline
                                rows={3}
                                value={formData.description}
                                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                            />
                        </Stack>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={closeForm}>Cancel</Button>
                        <Button type="submit" variant="contained">
                            Save
                        </Button>
                    </DialogActions>
                </form>
            </Dialog>

            <Dialog open={openDeleteConfirm} onClose={() => setOpenDeleteConfirm(false)}>
                <Box p={3} textAlign="center">
                    <Typography variant="h6">Delete Category?</Typography>
                    <Stack direction="row" spacing={2} mt={3}>
                        <Button fullWidth variant="outlined" onClick={() => setOpenDeleteConfirm(false)}>
                            Cancel
                        </Button>
                        <Button fullWidth variant="contained" color="error" onClick={() => deleteMutation.mutate(selectedId)}>
                            Delete
                        </Button>
                    </Stack>
                </Box>
            </Dialog>

            <Snackbar open={toast.open} autoHideDuration={3000} onClose={() => setToast({ ...toast, open: false })}>
                <Alert severity={toast.severity}>{toast.message}</Alert>
            </Snackbar>
        </Box>
    );
}
